<template>
  <form
    class="ai-form"
    @submit.prevent="save"
  >
    <div class="ai-form__intro">
      <h3>Настройки ассистента</h3>
      <p>{{ current.intro }}</p>
    </div>

    <p
      v-if="loading"
      class="ai-form__muted"
    >
      <span class="ai-spinner ai-spinner--small" />
      Загружаю настройки…
    </p>
    <template v-else-if="payload">
      <p
        v-if="locked && current.key !== 'instructions'"
        class="ai-form__note"
      >
        Настройки закреплены администратором сервера и здесь только отображаются.
      </p>

      <div
        class="ai-segmented ai-form__tabs"
        role="tablist"
      >
        <button
          v-for="item in tabs"
          :key="item.key"
          type="button"
          role="tab"
          :aria-selected="current.key === item.key ? 'true' : 'false'"
          :class="{ 'is-active': current.key === item.key }"
          @click="$emit('update:tab', item.key)"
        >
          {{ item.label }}
          <span
            v-if="dirty[item.section]"
            class="ai-form__dot"
            title="Есть несохранённые изменения"
          />
        </button>
      </div>

      <!-- Model -->
      <template v-if="current.key === 'model'">
        <fieldset
          class="ai-form__group"
          :disabled="locked"
        >
          <legend>Подключение</legend>
          <settings-field
            v-slot="{ id }"
            label="Адрес API"
            v-bind="mark('model', 'baseUrl')"
            @reset="resetFields('model', ['baseUrl'])"
          >
            <input
              :id="id"
              v-model.trim="form.baseUrl"
              class="ai-input"
              placeholder="http://127.0.0.1:1234/v1"
              @change="models = null"
            >
          </settings-field>
          <settings-field
            v-slot="{ id }"
            label="Ключ API"
            :hint="apiKeySet ? 'задан — оставьте пустым, чтобы не менять' : 'не задан'"
            v-bind="mark('model', 'apiKey')"
            @reset="resetFields('model', ['apiKey'])"
          >
            <input
              :id="id"
              v-model="apiKey"
              type="password"
              class="ai-input"
              autocomplete="new-password"
              :placeholder="apiKeySet ? '••••••••' : 'Если сервер требует ключ'"
            >
          </settings-field>
          <settings-field
            v-slot="{ id }"
            inline
            label="Разрешить внешний сервер"
            v-bind="mark('model', 'allowExternal')"
            @reset="resetFields('model', ['allowExternal'])"
          >
            <label
              class="ai-check"
              :for="id"
            >
              <input
                :id="id"
                v-model="form.allowExternal"
                type="checkbox"
              >
              <span>Разрешить внешний (не локальный) сервер</span>
            </label>
          </settings-field>
        </fieldset>

        <fieldset
          class="ai-form__group"
          :disabled="locked"
        >
          <legend>
            <span>Модели</span>
            <button
              type="button"
              class="ai-button is-small is-ghost"
              :disabled="modelsLoading"
              @click="loadModels"
            >
              <span
                v-if="modelsLoading"
                class="ai-spinner ai-spinner--small"
              />
              {{ modelsLoading ? 'Загружаю…' : 'Обновить список' }}
            </button>
          </legend>
          <p
            v-if="modelsError"
            class="ai-form__error"
          >
            {{ modelsError }}
          </p>
          <settings-field
            v-for="field in modelFields"
            :key="field.key"
            v-slot="{ id }"
            :label="field.label"
            v-bind="mark('model', field.key)"
            @reset="resetFields('model', [field.key])"
          >
            <select
              v-if="models && models.length"
              :id="id"
              v-model="form[field.key]"
              class="ai-input"
            >
              <option value="">
                {{ field.optional ? 'Та же, что для чата' : autoLabel }}
              </option>
              <option
                v-for="model in modelOptions(form[field.key])"
                :key="model.id"
                :value="model.id"
              >
                {{ modelLabel(model) }}
              </option>
            </select>
            <input
              v-else
              :id="id"
              v-model.trim="form[field.key]"
              class="ai-input"
              :placeholder="field.optional ? 'Та же, что для чата' : 'Пусто — автоматически'"
            >
          </settings-field>
          <p
            v-if="selectedCaps.length"
            class="ai-form__caps"
          >
            <span>Модель чата понимает:</span>
            <span
              v-for="cap in selectedCaps"
              :key="cap"
              class="ai-form__cap"
            >{{ cap }}</span>
          </p>
          <div
            v-if="selected && selected.loaded === false && serverKind === 'lmstudio'"
            class="ai-form__note"
          >
            Модель не загружена в LM Studio.
            <button
              type="button"
              class="ai-button is-small"
              :disabled="loadingModel"
              @click="loadSelected"
            >
              <span
                v-if="loadingModel"
                class="ai-spinner ai-spinner--small"
              />
              Загрузить
            </button>
          </div>
        </fieldset>

        <fieldset
          class="ai-form__group"
          :disabled="locked"
        >
          <legend>Параметры</legend>
          <div class="ai-form__grid">
            <settings-field
              v-for="field in numberFields"
              :key="field.key"
              v-slot="{ id }"
              :label="field.label"
              :title="field.hint"
              v-bind="mark('model', field.key)"
              @reset="resetFields('model', [field.key])"
            >
              <input
                :id="id"
                v-model.number="form[field.key]"
                type="number"
                class="ai-input"
                :min="field.min"
                :max="field.max"
                :step="field.step || 1"
                :placeholder="field.placeholder || 'Авто'"
              >
            </settings-field>
          </div>
        </fieldset>

        <div
          v-if="test"
          class="ai-form__result ai-tone"
          :class="test.ok ? 'is-success' : 'is-danger'"
        >
          <strong>{{ test.ok ? 'Проверка пройдена' : 'Есть проблемы' }}</strong>
          <span v-if="test.error">{{ test.error }}</span>
          <span v-if="test.latencyMs != null">
            Сервер ответил за {{ test.latencyMs }} мс, моделей: {{ test.models }}.
          </span>
          <span v-if="test.autoModel">Модель выбирается автоматически: {{ test.autoModel }}.</span>
          <span
            v-for="item in test.problems || []"
            :key="item"
          >
            {{ item }}
          </span>
          <span v-if="test.generation && test.generation.ok">
            Пробный ответ за {{ test.generation.seconds }} с
            <template v-if="test.generation.tokensPerSecond">, {{ test.generation.tokensPerSecond }} ток/с</template>
            .
          </span>
          <span v-else-if="test.generation">
            Пробный ответ не получен: {{ test.generation.error || 'пустой ответ' }}
          </span>
          <span
            v-for="line in mediaLines"
            :key="line"
          >
            {{ line }}
          </span>
          <span v-if="test.context && test.context.limit">
            Контекст модели: {{ test.context.limit.toLocaleString('ru-RU') }} токенов.
          </span>
          <span v-if="test.warning">{{ test.warning }}</span>
        </div>
      </template>

      <!-- Organization instructions (a separate store of the service, not part of the settings) -->
      <template v-else-if="current.key === 'instructions'">
        <p
          v-if="instructionsLoading"
          class="ai-form__muted"
        >
          <span class="ai-spinner ai-spinner--small" />
          Загружаю инструкции…
        </p>
        <div
          v-else-if="instructions"
          class="ai-field"
        >
          <label
            class="ai-field__label"
            for="ai-instructions"
          >
            Инструкции для ассистента
            <span
              class="ai-field__hint"
              :class="{ 'ai-form__over': instructionsText.length > maxInstructions }"
            >{{ instructionsText.length }}/{{ maxInstructions }}</span>
          </label>
          <textarea
            id="ai-instructions"
            v-model="instructionsText"
            class="ai-input ai-form__instructions"
            rows="12"
            :readonly="!instructions.canEdit"
            :placeholder="instructionsPlaceholder"
            @input="instructionsSavedAt = null"
          />
          <span class="ai-field__hint">{{ instructionsChanged }}</span>
        </div>
        <p
          v-if="instructionsError"
          class="ai-form__error"
        >
          {{ instructionsError }}
        </p>
        <p
          v-if="instructionsSavedAt"
          class="ai-form__muted"
        >
          Сохранено.
        </p>
      </template>

      <!-- Agent limits -->
      <template v-else-if="current.key === 'limits'">
        <fieldset
          v-for="group in limitGroups"
          :key="group.title"
          class="ai-form__group"
          :disabled="locked"
        >
          <legend>{{ group.title }}</legend>
          <div class="ai-form__grid">
            <settings-field
              v-for="field in group.fields"
              :key="field.key"
              v-slot="{ id }"
              :label="field.label"
              :note="limitNote(field)"
              v-bind="mark('agent', field.key)"
              @reset="resetFields('agent', [field.key])"
            >
              <input
                :id="id"
                v-model.number="agent[field.key]"
                type="number"
                class="ai-input"
                :min="field.min"
                :max="field.max"
              >
            </settings-field>
          </div>
        </fieldset>
      </template>

      <p
        v-if="error"
        class="ai-form__error"
      >
        {{ error }}
      </p>
      <p
        v-if="savedAt"
        class="ai-form__muted"
      >
        {{ saveWarning ? `Сохранено. ${saveWarning}` : 'Сохранено.' }}
      </p>

      <div class="ai-form__actions">
        <confirm-inline
          v-if="confirmingReset && !locked && current.key !== 'instructions'"
          class="ai-form__reset"
          :text="`Вернуть значения по умолчанию на вкладке «${current.label}»?`"
          action="Вернуть"
          :busy="resetting"
          @confirm="resetSection"
          @cancel="confirmingReset = false"
        />
        <button
          v-else-if="!locked && current.key !== 'instructions'"
          type="button"
          class="ai-button is-ghost ai-form__reset"
          :disabled="!changed[current.section].length || resetting"
          @click="confirmingReset = true"
        >
          <ai-icon
            name="reset"
            :size="14"
          />
          Вернуть значения по умолчанию
        </button>
        <button
          type="button"
          class="ai-button"
          @click="$emit('close')"
        >
          Закрыть
        </button>
        <button
          v-if="current.key === 'model'"
          type="button"
          class="ai-button"
          :disabled="testing"
          @click="runTest"
        >
          <span
            v-if="testing"
            class="ai-spinner ai-spinner--small"
          />
          Проверить
        </button>
        <button
          v-if="current.key === 'instructions'"
          type="button"
          class="ai-button is-primary"
          :disabled="!canSaveInstructions"
          @click="saveInstructions"
        >
          <span
            v-if="instructionsSaving"
            class="ai-spinner ai-spinner--small"
          />
          Сохранить
        </button>
        <button
          v-else-if="!locked"
          type="submit"
          class="ai-button is-primary"
          :disabled="saving || !anyDirty"
        >
          Сохранить
        </button>
      </div>
    </template>
    <p
      v-else
      class="ai-form__error"
    >
      {{ error }}
    </p>
  </form>
</template>

<script>
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import ConfirmInline from './ConfirmInline.vue';
  import SettingsField from './SettingsField.vue';
  import { MAX_INSTRUCTIONS, authorLabel, memoryDate } from './memory';

  const TABS = [
    {
      key: 'model',
      section: 'model',
      label: 'Модель',
      intro: 'Подходит любой сервер с OpenAI-совместимым API: LM Studio, llama.cpp, vLLM, Ollama и другие.',
    },
    {
      key: 'limits',
      section: 'agent',
      label: 'Лимиты',
      intro: 'Сколько шагов, запросов к данным и времени ассистент может потратить на ответ или расследование.',
    },
    {
      key: 'instructions',
      section: 'instructions',
      label: 'Инструкции',
      intro: 'Общие правила для всех ответов ассистента, например как называть сотрудников или какие отделы важны.',
    },
  ];
  const MODEL_FIELDS = [
    { key: 'chatModel', label: 'Модель для чата' },
    { key: 'investigationModel', label: 'Модель для расследований', optional: true },
  ];
  const NUMBER_FIELDS = [
    {
      key: 'contextLength',
      label: 'Контекст, токенов',
      min: 2048,
      max: 4000000,
      hint: 'Пусто — как у загруженной модели',
    },
    { key: 'answerTokens', label: 'Лимит ответа чата', min: 256, max: 200000 },
    { key: 'reportTokens', label: 'Лимит отчёта расследования', min: 256, max: 200000 },
    { key: 'reasoningBudget', label: 'Бюджет рассуждений', min: 0, max: 200000, hint: '0 — отключить рассуждения' },
    { key: 'temperature', label: 'Температура', min: 0, max: 2, step: 0.05 },
    {
      key: 'compactAt',
      label: 'Сжатие истории, доля',
      min: 0.3,
      max: 0.95,
      step: 0.05,
      hint: 'Доля контекста, после которой история сжимается',
    },
    { key: 'requestTimeout', label: 'Тайм-аут запроса, с', min: 10, max: 3600 },
  ];
  const EDITABLE = [
    'baseUrl',
    'chatModel',
    'investigationModel',
    'allowExternal',
    ...NUMBER_FIELDS.map((f) => f.key),
  ];
  const LIMIT_GROUPS = [
    {
      title: 'Чат',
      fields: [
        { key: 'chatMaxSteps', label: 'Шагов', min: 1, max: 100 },
        { key: 'chatMaxToolCalls', label: 'Вызовов инструментов', min: 1, max: 500 },
        { key: 'chatTimeout', label: 'Время ответа, с', min: 30, max: 86400, time: true },
      ],
    },
    {
      title: 'Расследование',
      fields: [
        { key: 'maxIterations', label: 'Шагов', min: 1, max: 1000 },
        { key: 'maxToolCalls', label: 'Вызовов инструментов', min: 1, max: 5000 },
        { key: 'agentTimeout', label: 'Время работы, с', min: 60, max: 86400, time: true },
      ],
    },
    {
      title: 'Данные',
      fields: [
        { key: 'maxRecordsPerTool', label: 'Записей за запрос', min: 100, max: 200000 },
        { key: 'maxToolResultChars', label: 'Размер результата инструмента', min: 2000, max: 400000, unit: 'символов' },
      ],
    },
  ];
  const INSTRUCTIONS_PLACEHOLDER =
    'Например: «Сотрудников называй по фамилии и инициалам. Отдел продаж и бухгалтерия — главные, ' +
    'о них отвечай подробнее.»';
  const AGENT_KEYS = LIMIT_GROUPS.flatMap((group) => group.fields.map((field) => field.key));

  const blank = (value) => (value == null ? '' : value);

  // Form values of the sections from a settings answer of the service.
  const FORMS = {
    model(data) {
      const settings = data.settings || {};
      const form = EDITABLE.reduce((result, key) => ({ ...result, [key]: blank(settings[key]) }), {});
      form.allowExternal = !!settings.allowExternal;
      return form;
    },
    agent(data) {
      const agent = data.agent || {};
      return AGENT_KEYS.reduce((result, key) => ({ ...result, [key]: blank(agent[key]) }), {});
    },
  };
  const FORM_OF = { model: 'form', agent: 'agent' };

  /** The model the service picks when none is set (as Discovery.pick_default does). */
  function pickDefault(models) {
    const rules = [(m) => m.loaded && m.toolUse, (m) => m.loaded, (m) => m.toolUse];
    for (let i = 0; i < rules.length; i += 1) {
      const found = models.find(rules[i]);
      if (found) return found;
    }
    return models.length === 1 ? models[0] : null;
  }

  function outOfRange(field, value) {
    return value === '' || typeof value !== 'number' || value < field.min || value > field.max;
  }

  export default {
    name: 'SettingsPanel',
    components: { AiIcon, ConfirmInline, SettingsField },
    props: {
      // Tab key (model | limits | instructions), changed by the panel via update:tab.
      tab: { type: String, default: 'model' },
    },
    data: () => ({
      loading: true,
      payload: null,
      form: null,
      agent: null,
      apiKey: '',
      models: null,
      serverKind: '',
      modelsLoading: false,
      modelsError: '',
      loadingModel: false,
      testing: false,
      test: null,
      saving: false,
      resetting: false,
      // «Вернуть значения по умолчанию» is being confirmed.
      confirmingReset: false,
      savedAt: null,
      saveWarning: '',
      error: '',
      tabs: TABS,
      modelFields: MODEL_FIELDS,
      numberFields: NUMBER_FIELDS,
      limitGroups: LIMIT_GROUPS,
      // GET /instructions: {text, updatedAt, updatedBy, canEdit}.
      instructions: null,
      instructionsText: '',
      instructionsLoading: true,
      instructionsSaving: false,
      instructionsSavedAt: null,
      instructionsError: '',
      instructionsPlaceholder: INSTRUCTIONS_PLACEHOLDER,
      maxInstructions: MAX_INSTRUCTIONS,
    }),
    computed: {
      current() {
        return TABS.find((item) => item.key === this.tab) || TABS[0];
      },
      locked() {
        return !!(this.payload && this.payload.locked);
      },
      changed() {
        return { model: [], agent: [], ...((this.payload && this.payload.changed) || {}) };
      },
      apiKeySet() {
        return !!this.payload.settings.apiKeySet;
      },
      saved() {
        return Object.keys(FORMS).reduce((result, name) => ({ ...result, [name]: FORMS[name](this.payload) }), {});
      },
      dirty() {
        if (!this.payload) return { model: false, agent: false };
        const differs = (section, keys) => keys.some((key) => this[FORM_OF[section]][key] !== this.saved[section][key]);
        return {
          model: differs('model', EDITABLE) || !!this.apiKey,
          agent: differs('agent', AGENT_KEYS),
          instructions: !!this.instructions && this.instructionsText.trim() !== this.instructions.text,
        };
      },
      anyDirty() {
        return this.dirty.model || this.dirty.agent;
      },
      selected() {
        return (this.models || []).find((m) => m.id === this.form.chatModel) || null;
      },
      // What the chat model accepts besides text (null: the server does not say).
      selectedCaps() {
        const model = this.selected;
        if (!model) return [];
        return [model.vision && 'зрение', model.audio && 'звук'].filter(Boolean);
      },
      autoLabel() {
        let name;
        if (this.payload.autoModel && this.form.baseUrl === this.saved.model.baseUrl) name = this.payload.autoModel;
        else if (this.models) name = (pickDefault(this.models) || {}).id;
        else return 'Автоматически';
        return name ? `Автоматически — ${name}` : 'Автоматически — нет загруженной модели';
      },
      // Whether the chat model takes images and sound (the check asks the model itself).
      mediaLines() {
        const { vision, audio } = this.test || {};
        const line = (label, value) => {
          if (!value) return '';
          if (value.ok) return `${label}: да.`;
          return `${label}: нет${value.note ? ` — ${value.note}` : ''}.`;
        };
        return [line('Изображения', vision), line('Звук', audio)].filter(Boolean);
      },
      canSaveInstructions() {
        const { instructions } = this;
        return !!(instructions && instructions.canEdit) && !this.instructionsSaving && this.dirty.instructions
          && this.instructionsText.length <= MAX_INSTRUCTIONS;
      },
      instructionsChanged() {
        const { updatedAt, updatedBy } = this.instructions || {};
        if (!updatedAt) return 'Инструкции ещё не задавались.';
        const author = authorLabel(updatedBy, this.$store.getters['user/userId']);
        return `Последнее изменение: ${memoryDate(updatedAt)}${author ? `, ${author}` : ''}`;
      },
    },
    watch: {
      tab() {
        this.confirmingReset = false;
      },
    },
    async created() {
      this.loadInstructions();
      try {
        const { data } = await api.getSettings();
        this.apply(data);
        this.loadModels();
      } catch (error) {
        this.error = api.errorText(error, 'Не удалось загрузить настройки');
      } finally {
        this.loading = false;
      }
    },
    methods: {
      /** Takes a settings answer; with a section (and fields) only those form values are replaced. */
      apply(data, section = null, fields = null) {
        this.payload = data;
        (section ? [section] : Object.keys(FORMS)).forEach((name) => {
          const fresh = FORMS[name](data);
          const target = FORM_OF[name];
          if (!fields || !this[target]) this[target] = fresh;
          else fields.filter((key) => key in fresh).forEach((key) => this.$set(this[target], key, fresh[key]));
        });
        const touches = (name, key) => (!section || section === name) && (!fields || fields.includes(key));
        if (touches('model', 'apiKey')) this.apiKey = '';
      },
      mark(section, key) {
        return { changed: this.changed[section].includes(key), locked: this.locked, busy: this.resetting };
      },
      range(field) {
        return `${field.min.toLocaleString('ru-RU')}–${field.max.toLocaleString('ru-RU')}`;
      },
      // Under a limit: its range and, for times, the value in hours and minutes.
      limitNote(field) {
        const note = `Допустимо ${this.range(field)}${field.unit ? ` ${field.unit}` : ''}`;
        const value = this.agent[field.key];
        if (!field.time || typeof value !== 'number' || value < 60) return note;
        const hours = Math.floor(value / 3600);
        const minutes = Math.round((value % 3600) / 60);
        const parts = [hours && `${hours} ч`, minutes && `${minutes} мин`].filter(Boolean);
        return `${note} · сейчас ${parts.join(' ')}`;
      },
      body() {
        const body = {};
        EDITABLE.forEach((key) => {
          const value = this.form[key];
          body[key] = value === '' ? null : value;
        });
        if (body.chatModel === null) body.chatModel = '';
        if (body.investigationModel === null) body.investigationModel = '';
        if (this.apiKey) body.apiKey = this.apiKey;
        return body;
      },
      agentPatch() {
        return AGENT_KEYS.reduce(
          (patch, key) => (this.agent[key] !== this.saved.agent[key] ? { ...patch, [key]: this.agent[key] } : patch),
          {},
        );
      },
      validate() {
        if (this.dirty.agent) {
          const wrong = LIMIT_GROUPS.flatMap((group) => group.fields.map((field) => ({ ...field, group: group.title })))
            .find((field) => outOfRange(field, this.agent[field.key]));
          if (wrong) {
            return { tab: 'limits', message: `${wrong.group} — «${wrong.label}»: допустимо ${this.range(wrong)}.` };
          }
        }
        return null;
      },
      modelOptions(current) {
        const list = this.models || [];
        return current && !list.some((m) => m.id === current) ? [{ id: current, name: current }, ...list] : list;
      },
      modelLabel(model) {
        const parts = [model.name && model.name !== model.id ? `${model.name} (${model.id})` : model.id];
        if (model.loaded === true) parts.push('загружена');
        if (model.vision) parts.push('зрение');
        if (model.audio) parts.push('звук');
        if (model.maxContextLength) parts.push(`до ${Math.round(model.maxContextLength / 1024)}K`);
        return parts.join(' · ');
      },
      async loadModels() {
        this.modelsLoading = true;
        this.modelsError = '';
        try {
          const { data } = await api.listModels(this.body());
          this.models = data.models || [];
          this.serverKind = data.kind || '';
        } catch (error) {
          this.models = null;
          this.modelsError = api.errorText(error, 'Не удалось получить список моделей');
        } finally {
          this.modelsLoading = false;
        }
      },
      async loadSelected() {
        this.loadingModel = true;
        this.error = '';
        try {
          await api.loadModel({ model: this.form.chatModel, contextLength: this.form.contextLength || null });
          await this.loadModels();
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось загрузить модель');
        } finally {
          this.loadingModel = false;
        }
      },
      async runTest() {
        this.testing = true;
        this.test = null;
        try {
          const { data } = await api.testSettings({ ...this.body(), generate: true });
          this.test = data;
        } catch (error) {
          this.test = { ok: false, error: api.errorText(error, 'Проверка не выполнена') };
        } finally {
          this.testing = false;
        }
      },
      async save() {
        const problem = this.validate();
        this.error = problem ? problem.message : '';
        this.savedAt = null;
        if (problem) {
          this.$emit('update:tab', problem.tab);
          return;
        }
        const body = this.dirty.model ? this.body() : {};
        if (this.dirty.agent) body.agent = this.agentPatch();
        this.saving = true;
        try {
          const { data } = await api.saveSettings(body);
          this.apply(data);
          this.saveWarning = data.warning || '';
          this.savedAt = Date.now();
          this.$emit('saved');
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось сохранить настройки');
        } finally {
          this.saving = false;
        }
      },
      async resetFields(section, fields) {
        this.resetting = true;
        this.error = '';
        this.savedAt = null;
        try {
          const { data } = await api.resetSettings(section, fields || undefined);
          this.apply(data, section, fields);
          this.$emit('saved');
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось вернуть значения по умолчанию');
        } finally {
          this.resetting = false;
        }
      },
      async loadInstructions() {
        this.instructionsLoading = true;
        try {
          const { data } = await api.getInstructions();
          this.instructions = data;
          this.instructionsText = data.text || '';
        } catch (error) {
          this.instructionsError = api.errorText(error, 'Не удалось загрузить инструкции');
        } finally {
          this.instructionsLoading = false;
        }
      },
      async saveInstructions() {
        this.instructionsSaving = true;
        this.instructionsError = '';
        this.instructionsSavedAt = null;
        try {
          const { data } = await api.saveInstructions(this.instructionsText.trim());
          this.instructions = { ...this.instructions, ...data };
          this.instructionsText = data.text || '';
          this.instructionsSavedAt = Date.now();
        } catch (error) {
          this.instructionsError = api.errorText(error, 'Не удалось сохранить инструкции');
        } finally {
          this.instructionsSaving = false;
        }
      },
      async resetSection() {
        await this.resetFields(this.current.section, null);
        this.confirmingReset = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-form {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 14px;
    min-height: 0;
    padding: 16px 18px;
    overflow-y: auto;
  }

  .ai-form__intro {
    h3 {
      margin: 0 0 4px;
      color: var(--ui-text);
      font-size: 16px;
      font-weight: 500;
    }

    p {
      margin: 0;
      color: var(--ui-text-muted);
      font-size: 13px;
    }
  }

  .ai-form__tabs {
    align-self: flex-start;
    max-width: 100%;
    overflow-x: auto;

    // On a phone the tabs scroll sideways instead of wrapping their labels.
    button {
      white-space: nowrap;
    }
  }

  .ai-form__dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 4px;
    border-radius: 50%;
    background: var(--ui-primary);
    vertical-align: 2px;
  }


  .ai-form__group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
    margin: 0;
    padding: 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);

    // The group title is a header row inside the frame (a floated legend is laid out like an
    // ordinary child), so an action next to it never sits on the frame line.
    legend {
      display: flex;
      float: left;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      box-sizing: border-box;
      width: 100%;
      min-height: 26px;
      padding: 0;
      color: var(--ui-text);
      font-weight: 500;
    }
  }

  .ai-form__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px 12px;
  }


  .ai-form__note {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 8px 10px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface-soft);
    color: var(--ui-text-muted);
    font-size: 13px;
  }




  .ai-form__caps {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: -2px 0 0;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-form__cap {
    padding: 0 7px;
    border-radius: 999px;
    background: var(--ui-primary-soft);
    color: var(--ui-primary);
    line-height: 18px;
  }


  .ai-form__instructions {
    min-height: 220px;
    line-height: 1.5;
  }

  .ai-field__hint.ai-form__over {
    color: var(--ui-danger);
  }

  .ai-form__result {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    border-radius: var(--ui-radius-sm);
    font-size: 13px;
  }

  .ai-form__muted,
  .ai-form__error {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-form__error {
    color: var(--ui-danger);
  }

  .ai-form__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }

  .ai-form__reset {
    margin-right: auto;
  }
</style>
